import { API_BASE_URL } from '../config';

const ADMIN_AGENT_API_BASE_URL = `${API_BASE_URL}/api/admin-agent`;

export class AdminAgentError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'AdminAgentError';
  }
}

export interface SSEEvent {
  type: 'text' | 'tool_call' | 'render' | 'error' | 'done';
  content?: string;
  name?: string;
  component?: 'table' | 'form' | 'chart';
  props?: Record<string, any>;
}

export interface FormSubmitResult {
  success: boolean;
  result?: any;
  error?: string;
}

async function readErrorMessage(response: Response): Promise<string> {
  const fallbackMessage = response.status === 503
    ? 'Admin agent is currently unavailable. Please try again shortly.'
    : response.status === 502
      ? 'Admin agent returned an invalid response.'
      : response.status === 400
        ? 'The request could not be processed.'
        : `Admin agent request failed with status ${response.status}.`;

  const contentType = response.headers.get('content-type') || '';

  try {
    if (contentType.includes('application/json')) {
      const payload = await response.json() as { error?: unknown };
      if (typeof payload.error === 'string' && payload.error.trim()) {
        return payload.error.trim();
      }
    } else {
      const text = (await response.text()).trim();
      if (text) {
        return text;
      }
    }
  } catch {
    // Fall back to a safe message.
  }

  return fallbackMessage;
}

async function streamAgentSSE(
  path: string,
  body: Record<string, unknown>,
  onEvent: (event: SSEEvent) => void,
): Promise<void> {
  let response: Response;

  try {
    response = await fetch(`${ADMIN_AGENT_API_BASE_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (error) {
    throw new AdminAgentError(
      0,
      `Could not reach the application backend. ${error instanceof Error ? error.message : 'Please try again.'}`,
    );
  }

  if (!response.ok) {
    throw new AdminAgentError(response.status, await readErrorMessage(response));
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new AdminAgentError(502, 'Admin agent returned an empty streaming response.');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const event: SSEEvent = JSON.parse(line.slice(6));
          onEvent(event);
        } catch {
          // Skip malformed events
        }
      }
    }
  }
}

export async function sendMessage(
  message: string,
  sessionId: string,
  onEvent: (event: SSEEvent) => void,
): Promise<void> {
  await streamAgentSSE('/chat', { message, session_id: sessionId }, onEvent);
}

/**
 * Ask the coach agent to analyse the user's most recent completed workout. The
 * backend assembles the workout data and prompt, then streams the analysis back
 * using the same SSE protocol as {@link sendMessage}.
 */
export async function analyzeLastWorkout(
  sessionId: string,
  onEvent: (event: SSEEvent) => void,
): Promise<void> {
  await streamAgentSSE('/analyze-last-workout', { session_id: sessionId }, onEvent);
}

export async function submitForm(
  formId: string,
  values: Record<string, any>,
): Promise<FormSubmitResult> {
  try {
    const response = await fetch(`${ADMIN_AGENT_API_BASE_URL}/form-handler`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ form_id: formId, values }),
    });

    if (!response.ok) {
      return {
        success: false,
        error: await readErrorMessage(response),
      };
    }

    const payload = await response.json();
    if (payload && typeof payload === 'object' && 'success' in payload) {
      return payload as FormSubmitResult;
    }

    return {
      success: true,
      result: payload,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error
        ? error.message
        : 'Could not submit the admin agent form.',
    };
  }
}
